import { daysAgo, daysAhead } from './dataset';
import { REVIEWS } from './reviews';

// Reputation pages: Reviews Dashboard, Location Ratings, Sentiment Analysis, Auto Response,
// Competitor Reviews, Review Control. (ReviewsInbox™ is in reviews.js.)
const body = config => {
	const d = config && config.data;
	if (!d) return {};
	if (typeof d === 'object') return d;

	try {
		return JSON.parse(d);
	} catch (e) {
		return {};
	}
};

const locationSummary = (storeCode, locationName, addressLine, city, state, groups, reviewsCount, averageRating, replyCount, happinessRate, wordRatio, overallReviewCount, overallRating) => ({
	storeCode,
	locationName,
	addressLine,
	city,
	state,
	country: 'US',
	groups,
	reviewsCount,
	averageRating,
	replyCount,
	happinessRate,
	wordRatio,
	overallReviewCount,
	overallRating
});

const LOCATION_SUMMARY = [locationSummary('BC-101', 'Riverside Plaza', '1420 Riverside Ave', 'Seattle', 'WA', 'Washington cafés', 72, 4.7, 52, 84, 78, 1240, 4.6), locationSummary('BC-102', 'Northgate Center', '88 Northgate Blvd', 'Portland', 'OR', 'Oregon cafés', 58, 4.5, 40, 80, 74, 986, 4.5), locationSummary('BC-103', 'Harbour Point', '300 Harbour Point Dr', 'Tacoma', 'WA', 'Washington cafés', 49, 4.3, 31, 75, 70, 812, 4.3), locationSummary('BC-104', 'Westfield Row', '22 Westfield Row', 'Bend', 'OR', 'Oregon cafés', 41, 4.1, 22, 70, 66, 655, 4.2), locationSummary('BC-105', 'Elm & 4th', '400 Elm St', 'Everett', 'WA', 'Washington cafés', 28, 3.8, 14, 62, 58, 402, 3.9)];

const stars = (five, four, three, two, one) => {
	const total = five + four + three + two + one;

	const pct = n => Math.round(n / total * 1000) / 10;

	return [{
		starRating: '5',
		count: five,
		percent: pct(five)
	}, {
		starRating: '4',
		count: four,
		percent: pct(four)
	}, {
		starRating: '3',
		count: three,
		percent: pct(three)
	}, {
		starRating: '2',
		count: two,
		percent: pct(two)
	}, {
		starRating: '1',
		count: one,
		percent: pct(one)
	}];
};

const OPEN_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

const periods = (days, openTime, closeTime) => days.map(d => ({
	openDay: d,
	closeDay: d,
	openTime,
	closeTime
}));

export default {
	// Reviews Dashboard — /getRecentReviews must have `result`.
	'/getReviewsOverview': () => ({
		reviews: {
			reviewCount: 248,
			avgReviews: 2.8,
			trendPercent: 12.4,
			locationsCount: 5
		},
		rating: {
			averageRating: 4.4,
			positiveRatingPercent: 82,
			trendPercent: 3.1
		},
		happiness: {
			happinessRate: 76.4,
			positiveRatingPercent: 82,
			neutralRatingPercent: 9,
			negativeRatingPercent: 9,
			trendPercent: 2.2
		},
		// "postive" is the spelling the page reads. All five entries are required.
		wordRatio: {
			postiveWordRatio: 71,
			trendPercent: -1.5,
			wordPercentByRating: [{
				wordPercent: 6
			}, {
				wordPercent: 4
			}, {
				wordPercent: 8
			}, {
				wordPercent: 24
			}, {
				wordPercent: 58
			}]
		},
		responseRatio: {
			responseRatio: 64,
			responseTime: 1.6,
			trendPercent: 8.0
		}
	}),
	'/getReviewsCount': () => ({
		sourceWiseRating: [{
			source: 'google',
			avg: 4.5,
			reviewCount: 156
		}, {
			source: 'yelp',
			avg: 4.1,
			reviewCount: 52
		}, {
			source: 'facebook',
			avg: 4.6,
			reviewCount: 40
		}]
	}),
	'/getAggregatedRatingCount': () => ({
		starRatings: stars(146, 58, 22, 9, 13)
	}),
	'/getAggregatedReplyRatingCount': () => ({
		replyStarRatings: stars(84, 38, 16, 8, 13)
	}),
	'/getRecentReviews': () => ({
		result: REVIEWS,
		total: REVIEWS.length
	}),
	// Location Ratings — /getLocationReviewSummaryList must have `locations`.
	'/getLocationRatings': () => ({
		starRating: [{
			range: '4.5-5',
			ratingPercent: 40,
			count: 2
		}, {
			range: '4-4.5',
			ratingPercent: 40,
			count: 2
		}, {
			range: '3.5-4',
			ratingPercent: 20,
			count: 1
		}]
	}),
	'/getLocationReviewSummaryList': () => ({
		total: LOCATION_SUMMARY.length,
		locations: LOCATION_SUMMARY
	}),
	'/getAllLocationReviewSummaryList': () => ({
		total: LOCATION_SUMMARY.length,
		locations: LOCATION_SUMMARY
	}),
	'/getGridOverview': () => ({
		reviews: {
			locationsCount: 5,
			reviewCount: 248,
			avgReviews: 2.8,
			trendPercent: 12.4
		},
		rating: {
			averageRating: 4.4,
			positiveRatingPercent: 82,
			trendPercent: 3.1
		},
		avgLocation: {
			avgLocationRating: 4.28,
			locationsAboveFour: 80,
			trendPercent: 1.9
		}
	}),
	// Sentiment Analysis — every topic needs all five sentiment keys.
	'/getSentimentOverview': () => ({
		totalTopics: 6,
		sentimentPieDistribution: [{
			sentiment: 'very positive',
			count: 96
		}, {
			sentiment: 'positive',
			count: 118
		}, {
			sentiment: 'neutral',
			count: 41
		}, {
			sentiment: 'negative',
			count: 29
		}, {
			sentiment: 'very negative',
			count: 14
		}]
	}),
	'/getSentimentByTopic': () => ({
		totalTopics: 6,
		topicXMin: -30,
		topicXMax: 80,
		topicDistribution: [{
			name: 'Coffee quality',
			'very negative': -2,
			negative: -6,
			neutral: 9,
			positive: 38,
			'very positive': 31
		}, {
			name: 'Staff friendliness',
			'very negative': -1,
			negative: -4,
			neutral: 6,
			positive: 29,
			'very positive': 35
		}, {
			name: 'Wait time',
			'very negative': -9,
			negative: -14,
			neutral: 7,
			positive: 12,
			'very positive': 5
		}, {
			name: 'Pastries',
			'very negative': -1,
			negative: -3,
			neutral: 5,
			positive: 17,
			'very positive': 14
		}, {
			name: 'Cleanliness',
			'very negative': -2,
			negative: -3,
			neutral: 8,
			positive: 15,
			'very positive': 9
		}, {
			name: 'Price',
			'very negative': -4,
			negative: -9,
			neutral: 10,
			positive: 7,
			'very positive': 3
		}]
	}),
	// Auto Response — /getAllARSettings must be a plain list.
	'/getAllARSettings': () => [{
		id: 'ar-5star-thanks',
		name: '5-star thank-you replies',
		description: 'Thank happy guests within a few hours, in the store voice.',
		campaignActive: true,
		alwaysActive: true,
		campaignPeriod: null,
		ratings: ['5'],
		comments: ['RATING_ONLY', 'RATING_WITH_SHORT_COMMENT', 'RATING_WITH_COMMENT'],
		responseRate: 100,
		reviewCount: 1,
		deliveryType: 'SCHEDULED',
		delay: {
			hour: 2,
			minute: 30
		},
		timeZone: '-08:00',
		dailySettings: {
			periods: periods(OPEN_DAYS, '07:00', '19:00')
		},
		filters: {
			sources: ['google', 'facebook'],
			organizations: [],
			locationFilters: {
				locationFilterIds: ['grp-wa-cafes']
			},
			reviewFilters: {
				languages: ['all']
			}
		},
		excludedGroupIds: [],
		totalCampaignLocationsCount: 3
	}, {
		id: 'ar-4star-followup',
		name: '4-star follow-ups',
		description: 'Short, warm replies to 4-star reviews.',
		campaignActive: true,
		alwaysActive: false,
		campaignPeriod: {
			from: daysAgo(30),
			to: daysAhead(60)
		},
		ratings: ['4'],
		comments: ['RATING_WITH_SHORT_COMMENT', 'RATING_WITH_COMMENT'],
		responseRate: 70,
		reviewCount: 2,
		deliveryType: 'SCHEDULED',
		delay: {
			hour: 4,
			minute: 0
		},
		timeZone: '-08:00',
		dailySettings: {
			periods: periods(['MONDAY', 'WEDNESDAY'], '09:00', '17:00')
		},
		filters: {
			sources: ['google', 'yelp'],
			organizations: ['accounts/loc-northgate'],
			locationFilters: {
				locationFilterIds: ['grp-or-cafes'],
				country: 'US',
				area: 'OR'
			},
			reviewFilters: {
				languages: ['EN']
			}
		},
		excludedGroupIds: ['grp-flagships'],
		totalCampaignLocationsCount: 2
	}, {
		id: 'ar-low-rating-approval',
		name: 'Low-rating approvals',
		description: 'Draft replies to 1–2 star reviews for Dana Whitfield to approve.',
		campaignActive: false,
		alwaysActive: true,
		campaignPeriod: null,
		ratings: ['1', '2'],
		comments: ['RATING_WITH_COMMENT'],
		responseRate: 100,
		reviewCount: 1,
		deliveryType: 'APPROVAL_QUEUE',
		delay: {
			hour: 0,
			minute: 30
		},
		timeZone: '-08:00',
		dailySettings: {
			periods: []
		},
		filters: {
			sources: ['google', 'yelp', 'facebook'],
			organizations: [],
			locationFilters: {
				locationFilterIds: []
			},
			reviewFilters: {
				languages: ['all']
			}
		},
		excludedGroupIds: [],
		totalCampaignLocationsCount: 5
	}],
	'/getAllReplyTemplates': () => ({
		topics: {
			'Thank you': 6,
			'Service recovery': 3,
			'Wait time': 2,
			'Seasonal menu': 2
		}
	}),
	// The Auto Response page saves profile settings by itself on load; answered here so nothing is written.
	'/saveProfileSettings': () => ({
		status: 200,
		message: 'Settings saved'
	}),
	// Review Control
	'/getEditors': () => [{
		userId: 'dana@brightsidecoffee.com',
		name: 'Dana Whitfield'
	}, {
		userId: 'marc@brightsidecoffee.com',
		name: 'Marc Oyelaran'
	}, {
		userId: 'priya@brightsidecoffee.com',
		name: 'Priya Raman'
	}],
	// 'QUEUE' / 'UPDATING' would poll every 10 seconds.
	'/getHistoricSentimentAnalysisStatus': () => [{
		fromDate: daysAgo(365).slice(0, 10),
		toDate: daysAgo(0).slice(0, 10),
		sources: ['google', 'yelp', 'facebook'],
		status: 'COMPLETED',
		percentage: 100,
		enableEmailNotification: true,
		isCompetitor: false
	}],
	// Location groups (own locations), or competitor groups when the request asks for them.
	'/getLocationTags': config => {
		const hits = body(config).isCompetitor ? [{
			filterTagId: 'grp-comp-wa',
			filterTagName: 'Washington competitors'
		}, {
			filterTagId: 'grp-comp-or',
			filterTagName: 'Oregon competitors'
		}] : [{
			filterTagId: 'grp-wa-cafes',
			filterTagName: 'Washington cafés'
		}, {
			filterTagId: 'grp-or-cafes',
			filterTagName: 'Oregon cafés'
		}, {
			filterTagId: 'grp-flagships',
			filterTagName: 'Flagship stores'
		}];
		return {
			status: 200,
			total: hits.length,
			hits
		};
	},
	// Competitor Reviews — all three are required: without them the page crashes or the spinner never stops.
	'/getCompetitorNames': () => ({
		status: 200,
		competitors: [{
			competitorName: 'Marlow & Sons',
			competitorImage: null,
			websiteLink: '',
			isLocationConnected: true
		}, {
			competitorName: 'Union Supply',
			competitorImage: null,
			websiteLink: '',
			isLocationConnected: true
		}, {
			competitorName: 'Pike Provisions',
			competitorImage: null,
			websiteLink: '',
			isLocationConnected: true
		}]
	}),
	// Own business first; competitor locationName must equal its competitorName.
	'/getTopCompetitors': () => ({
		isCompetitorIntegrated: true,
		CompetitorResult: [{
			locationName: 'Brightside Coffee',
			locationsCount: 5,
			isLocationConnected: true,
			avgStarRating: 4.4,
			totalReviews: 486,
			totalReply: 311,
			starRating: [{
				star: 1,
				value: 25
			}, {
				star: 2,
				value: 18
			}, {
				star: 3,
				value: 43
			}, {
				star: 4,
				value: 114
			}, {
				star: 5,
				value: 286
			}],
			sourceWiseReview: [{
				source: 'google',
				avgStarRating: 4.5,
				totalReviews: 306
			}, {
				source: 'yelp',
				avgStarRating: 4.1,
				totalReviews: 102
			}, {
				source: 'facebook',
				avgStarRating: 4.6,
				totalReviews: 78
			}]
		}, {
			locationName: 'Marlow & Sons',
			locationsCount: 3,
			isLocationConnected: true,
			avgStarRating: 4.2,
			totalReviews: 312,
			totalReply: 94,
			starRating: [{
				star: 1,
				value: 20
			}, {
				star: 2,
				value: 15
			}, {
				star: 3,
				value: 31
			}, {
				star: 4,
				value: 78
			}, {
				star: 5,
				value: 168
			}],
			sourceWiseReview: [{
				source: 'google',
				avgStarRating: 4.3,
				totalReviews: 204
			}, {
				source: 'yelp',
				avgStarRating: 4.0,
				totalReviews: 108
			}]
		}, {
			locationName: 'Union Supply',
			locationsCount: 2,
			isLocationConnected: true,
			avgStarRating: 3.9,
			totalReviews: 228,
			totalReply: 41,
			starRating: [{
				star: 1,
				value: 22
			}, {
				star: 2,
				value: 16
			}, {
				star: 3,
				value: 30
			}, {
				star: 4,
				value: 62
			}, {
				star: 5,
				value: 98
			}],
			sourceWiseReview: [{
				source: 'google',
				avgStarRating: 4.0,
				totalReviews: 150
			}, {
				source: 'yelp',
				avgStarRating: 3.7,
				totalReviews: 52
			}, {
				source: 'facebook',
				avgStarRating: 3.9,
				totalReviews: 26
			}]
		}, {
			locationName: 'Pike Provisions',
			locationsCount: 2,
			isLocationConnected: true,
			avgStarRating: 4.6,
			totalReviews: 174,
			totalReply: 120,
			starRating: [{
				star: 1,
				value: 5
			}, {
				star: 2,
				value: 3
			}, {
				star: 3,
				value: 8
			}, {
				star: 4,
				value: 30
			}, {
				star: 5,
				value: 128
			}],
			sourceWiseReview: [{
				source: 'google',
				avgStarRating: 4.7,
				totalReviews: 132
			}, {
				source: 'facebook',
				avgStarRating: 4.4,
				totalReviews: 42
			}]
		}]
	}),
	// All four lists are required (also used by Location Ratings grid view).
	'/getCompetitorReviewRating': () => ({
		profileData: [{
			brandName: 'Brightside Coffee',
			locationName: 'Brightside Coffee',
			totalReviews: 486,
			avgStarRating: 4.4
		}],
		competitorData: [{
			brandName: 'Marlow & Sons',
			locationName: 'Marlow & Sons',
			totalReviews: 312,
			avgStarRating: 4.2
		}, {
			brandName: 'Union Supply',
			locationName: 'Union Supply',
			totalReviews: 228,
			avgStarRating: 3.9
		}, {
			brandName: 'Pike Provisions',
			locationName: 'Pike Provisions',
			totalReviews: 174,
			avgStarRating: 4.6
		}],
		profileLocationData: LOCATION_SUMMARY.map(l => ({
			brandName: 'Brightside Coffee',
			locationName: l.locationName,
			locationAddress: `${l.city}, ${l.state}`,
			totalReviews: l.reviewsCount * 2,
			avgStarRating: l.averageRating
		})),
		competitorLocationData: [{
			brandName: 'Marlow & Sons',
			locationName: 'Marlow & Sons – Seattle',
			locationAddress: 'Seattle, WA',
			totalReviews: 120,
			avgStarRating: 4.3
		}, {
			brandName: 'Marlow & Sons',
			locationName: 'Marlow & Sons – Tacoma',
			locationAddress: 'Tacoma, WA',
			totalReviews: 104,
			avgStarRating: 4.2
		}, {
			brandName: 'Union Supply',
			locationName: 'Union Supply – Portland',
			locationAddress: 'Portland, OR',
			totalReviews: 132,
			avgStarRating: 3.9
		}, {
			brandName: 'Union Supply',
			locationName: 'Union Supply – Bend',
			locationAddress: 'Bend, OR',
			totalReviews: 96,
			avgStarRating: 3.8
		}, {
			brandName: 'Pike Provisions',
			locationName: 'Pike Provisions – Seattle',
			locationAddress: 'Seattle, WA',
			totalReviews: 98,
			avgStarRating: 4.7
		}]
	})
};